import { observer } from "mobx-react-lite";
import { AccountFontInput, FontInput } from "../../../API";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Upload, List, Divider, Select, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { font2Hash, FONT_LIBRARY } from "../../../utils/font";

import {
  fontInVideo2NestedFontFamily,
  extractFontsFromAccount,
  getVariantsFromFontFamily,
} from "./helpers";
import { RcFile } from "antd/lib/upload/interface";
import { post } from "aws-amplify/api";
import { NEW_REST_API } from "../../../consts";
import { IAccountModel } from "../../../stores/accountStore";

export type FontFamilySelectorProps = {
  fontsInVideoParts: FontInput[];
  fontOverrides: FontInput[];
  setFontOverrides: (fontOverrides: FontInput[]) => void;
  accountFonts: AccountFontInput[];
  account: IAccountModel | undefined;
  onUploadComplete: (newAccountFonts: AccountFontInput[]) => void;
  isUploadingFont: boolean;
  setIsUploadingFont: (isUploading: boolean) => void;
};

export const FontFamilySelector = observer(
  ({
    fontsInVideoParts,
    fontOverrides,
    setFontOverrides,
    accountFonts,
    account,
    onUploadComplete,
    isUploadingFont,
    setIsUploadingFont,
  }: FontFamilySelectorProps) => {
    const lastUploadedFontRef = useRef<{
      index: number;
      family: string;
    } | null>(null);

    const fontsInVideoPartsAsFontFamily = useMemo(() => {
      return fontInVideo2NestedFontFamily(fontsInVideoParts);
    }, [fontsInVideoParts]);

    const allFontFamilies = useMemo(() => {
      const uniqueAccountFonts = accountFonts.filter(
        (accFont) =>
          !fontsInVideoPartsAsFontFamily.some(
            (vidFont) => vidFont.fontFamily === accFont.fontFamily
          ) &&
          !FONT_LIBRARY.some(
            (libFont) => libFont.fontFamily === accFont.fontFamily
          )
      );

      return [
        ...FONT_LIBRARY,
        ...fontsInVideoPartsAsFontFamily.filter(
          (font) =>
            !FONT_LIBRARY.some(
              (libFont) => libFont.fontFamily === font.fontFamily
            )
        ),
        ...uniqueAccountFonts.map((font) => ({
          fontFamily: font.fontFamily,
          variants: font.variants.map((variant) => ({ ...variant })),
        })),
      ];
    }, [fontsInVideoPartsAsFontFamily, accountFonts]);

    const getVariantOptions = useCallback(
      (family: string) =>
        // @ts-ignore
        getVariantsFromFontFamily(family, allFontFamilies),
      [allFontFamilies]
    );

    const groupedFontOptions = useMemo(() => {
      const videoOptions = fontsInVideoPartsAsFontFamily.map((font) => ({
        label: font.fontFamily,
        value: font.fontFamily,
      }));
      const libraryOptions = FONT_LIBRARY.map((font) => ({
        label: font.fontFamily,
        value: font.fontFamily,
      }));
      const accountOptions = accountFonts.map((font) => ({
        label: font.fontFamily,
        value: font.fontFamily,
      }));

      const groups = [];
      if (videoOptions.length) {
        groups.push({ label: "Video Fonts", options: videoOptions });
      }
      if (libraryOptions.length) {
        groups.push({ label: "Fonts Library", options: libraryOptions });
      }
      if (accountOptions.length) {
        groups.push({ label: "Account Fonts", options: accountOptions });
      }
      return groups;
    }, [fontsInVideoPartsAsFontFamily, accountFonts]);

    const selectedUrls = useMemo(() => {
      return fontsInVideoParts.map((font) => {
        const fontInVideoPartHash = font2Hash(font);
        const overridesAsHash = fontOverrides.reduce<Record<string, FontInput>>(
          (acc, f) => {
            acc[font2Hash(f)] = f;
            return acc;
          },
          {}
        );
        return overridesAsHash[fontInVideoPartHash]?.url || font.url;
      });
    }, [fontsInVideoParts, fontOverrides]);

    const fontFamilyFromUrl = useCallback(
      (url: string) => {
        for (const fontFamily of allFontFamilies) {
          for (const variant of fontFamily.variants) {
            if (variant.url === url) {
              return fontFamily.fontFamily;
            }
          }
        }
        const originalFont = fontsInVideoParts.find((f) => f.url === url);
        const overrideSource = fontsInVideoParts.find(
          (f, idx) => selectedUrls[idx] === url
        );

        return originalFont?.family || overrideSource?.family || "";
      },
      [allFontFamilies, fontsInVideoParts, selectedUrls]
    );

    const [selectedVariantUrls, setSelectedVariantUrls] =
      useState<string[]>(selectedUrls);
    const [selectedFontFamilies, setSelectedFontFamilies] = useState<string[]>(
      () => selectedUrls.map(fontFamilyFromUrl)
    );

    useEffect(() => {
      const newSelectedUrls = fontsInVideoParts.map((font) => {
        const fontInVideoPartHash = font2Hash(font);
        const overridesAsHash = fontOverrides.reduce<Record<string, FontInput>>(
          (acc, f) => {
            acc[font2Hash(f)] = f;
            return acc;
          },
          {}
        );
        return overridesAsHash[fontInVideoPartHash]?.url || font.url;
      });
      setSelectedVariantUrls(newSelectedUrls);
      setSelectedFontFamilies(newSelectedUrls.map(fontFamilyFromUrl));
    }, [fontsInVideoParts, fontOverrides, fontFamilyFromUrl]);

    const handleFamilyChange = useCallback(
      (index: number, newFamily: string) => {
        const updatedFamilies = [...selectedFontFamilies];
        updatedFamilies[index] = newFamily;

        // @ts-ignore
        let newVariants = getVariantsFromFontFamily(newFamily, allFontFamilies);

        const originalVideoFont = fontsInVideoParts[index];
        const originalWeightStr = String(originalVideoFont.weight);
        const defaultVariant =
          newFamily === originalVideoFont.family
            ? newVariants.find(
                (v) =>
                  String(v.weight) === originalWeightStr &&
                  v.style === originalVideoFont.style
              ) || newVariants[0]
            : newVariants.find(
                (v) =>
                  String(v.weight) === "Regular" || String(v.weight) === "400"
              ) || newVariants[0];

        const defaultVariantUrl = defaultVariant?.url || "";

        const updatedVariants = [...selectedVariantUrls];
        updatedVariants[index] = defaultVariantUrl;

        setSelectedFontFamilies(updatedFamilies);
        setSelectedVariantUrls(updatedVariants);
      },
      [
        allFontFamilies,
        fontsInVideoParts,
        selectedFontFamilies,
        selectedVariantUrls,
      ]
    );

    const handleVariantChange = (index: number, newVariantUrl: string) => {
      const updatedVariants = [...selectedVariantUrls];
      updatedVariants[index] = newVariantUrl;
      setSelectedVariantUrls(updatedVariants);
    };

    useEffect(() => {
      const newOverrides = fontsInVideoParts.reduce(
        (acc, originalFont, index) => {
          if (
            selectedVariantUrls[index] &&
            selectedVariantUrls[index] !== originalFont.url
          ) {
            acc.push({
              family: originalFont.family,
              weight: originalFont.weight,
              style: originalFont.style,
              url: selectedVariantUrls[index],
            });
          }
          return acc;
        },
        [] as FontInput[]
      );

      if (JSON.stringify(newOverrides) !== JSON.stringify(fontOverrides)) {
        setFontOverrides(newOverrides);
      }
    }, [
      selectedVariantUrls,
      setFontOverrides,
      fontsInVideoParts,
      fontOverrides,
    ]);

    useEffect(() => {
      if (lastUploadedFontRef.current) {
        const { index, family } = lastUploadedFontRef.current;
        if (allFontFamilies.some((f) => f.fontFamily === family)) {
          handleFamilyChange(index, family);
        }
        lastUploadedFontRef.current = null;
      }
    }, [accountFonts, allFontFamilies, handleFamilyChange]);

    const handleFontUpload = async ({
      index,
      file,
      onSuccess,
      onError,
    }: any) => {
      try {
        setIsUploadingFont(true);

        if (!account?.id) {
          throw new Error("ERR_NO_ACCOUNT_ID");
        }

        if (!file) return;

        const readFileAsDataURL = (readFile: RcFile): Promise<string> => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              const dataUrl = e.target?.result;
              if (typeof dataUrl === "string") resolve(dataUrl);
              else reject(new Error("Failed to read file as Data URL."));
            };
            reader.onerror = reject;
            reader.readAsDataURL(readFile);
          });
        };

        const dataUrl = await readFileAsDataURL(file);
        const base64Data = dataUrl.split(",")[1];

        const myInit = {
          body: { font: { data: base64Data, fileName: file.name } },
          response: true,
        };

        const response = await post({
          apiName: NEW_REST_API,
          path: `/v2/private/account/${account.id}/font`,
          options: myInit,
        }).response;

        if (response.statusCode >= 200 && response.statusCode < 300) {
          const responseBody = (await response.body.json()) as { url: string };
          const fontUrl = responseBody?.url;
          if (fontUrl) {
            await account.refreshFonts();
            const latestAccountFonts = extractFontsFromAccount(account.fonts);
            const uploadedFontFamily = latestAccountFonts.find((f) =>
              f.variants.some((v) => v.url === fontUrl)
            );

            if (uploadedFontFamily) {
              lastUploadedFontRef.current = {
                index,
                family: uploadedFontFamily.fontFamily,
              };
            }
            onUploadComplete(latestAccountFonts);
          }
          onSuccess && onSuccess(responseBody, response);
          message.success(`${file.name} uploaded successfully`);
        } else {
          const errorBody = await response.body.text();
          console.error(
            `Font upload failed with status ${response.statusCode}:`,
            errorBody
          );
          throw new Error(
            `Font upload failed: ${errorBody || response.statusCode}`
          );
        }
      } catch (error: any) {
        message.error(`Upload failed: ${error.message || "Unknown error"}`);
        onError && onError(error);
      } finally {
        setIsUploadingFont(false);
      }
    };

    return (
      <div
        className="group-elements-wrapper"
        key={`font-family-selector-${account?.id || "account"}`}
      >
        <div className="group-elements-title" style={{ marginBottom: 8 }}>
          Fonts
        </div>
        <div className="group-elements-content">
          <div className="font-override-section">
            {isUploadingFont ? (
              <div className="font-override-section-loading">
                <span className="loading-text">Uploading font...</span>
                <span className="loader"></span>
              </div>
            ) : null}
            {fontsInVideoParts.map((fontVideo, index) => {
              const currentFamily = selectedFontFamilies[index] || "";
              const variantOptions = currentFamily
                ? getVariantOptions(currentFamily)
                : [];

              return (
                <div
                  key={`${fontVideo.family}-${fontVideo.weight}-${fontVideo.style}-${index}`}
                  className="font-override-item"
                >
                  <div style={{ display: "flex", width: "100%", gap: 8 }}>
                    <Select
                      value={currentFamily}
                      style={{ width: "50%" }}
                      disabled={isUploadingFont}
                      placeholder="Select a font family"
                      options={groupedFontOptions}
                      onChange={(newFamily) =>
                        handleFamilyChange(index, newFamily)
                      }
                      dropdownRender={(menu) => (
                        <div>
                          {menu}
                          <Divider style={{ margin: "0" }} />
                          <Upload
                            multiple={false}
                            disabled={isUploadingFont}
                            className="container-font-upload"
                            style={{ width: "100%", padding: "7px" }}
                            name="file"
                            accept=".ttf, .otf, .woff, .woff2"
                            customRequest={(options) => {
                              // @ts-ignore - AntD types might be tricky here
                              handleFontUpload({ index, ...options });
                            }}
                            showUploadList={false}
                          >
                            <List.Item className="fonts-final-list-item">
                              <UploadOutlined width={22} />
                              Add fonts
                            </List.Item>
                          </Upload>
                        </div>
                      )}
                    />
                    <Select
                      style={{ width: "50%" }}
                      value={selectedVariantUrls[index] || ""}
                      disabled={isUploadingFont || !currentFamily}
                      options={variantOptions.map((variant) => ({
                        label: variant.name,
                        value: variant.url,
                      }))}
                      onChange={(newVariantUrl) =>
                        handleVariantChange(index, newVariantUrl)
                      }
                      notFoundContent={
                        currentFamily
                          ? "No variants found"
                          : "Select a family first"
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
);
